import React, { useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { ReactNode } from 'react';

import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { noop } from 'ui-components/default-props/noop';

import { useModal } from '../../../hooks/use-modal';
import { useResolveConnectedProviderId } from '../../../hooks/freight/use-resolve-connected-provider-id';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { useTrackFreightConnectSucceeded } from '../../../tracking/use-track-freight-connect-succeeded';
import { useTrackFreightConnectFailed } from '../../../tracking/use-track-freight-connect-failed';
import { getFreightProviderTrackingSlug } from '../../../pages/settings/carriers/freight/constants';
import { FreightProviderRegistrationModal } from '.';

import { messages } from './messages';

// Never read error.message here — it can echo back user-entered account/contact details from the
// third-party connect form, which would leak PII into the LogRocket `reason` property.
const getSafeErrorReason = (error: unknown): string => {
  if (error && typeof error === 'object' && typeof (error as { code?: unknown }).code === 'string') {
    return (error as { code: string }).code;
  }
  if (error instanceof Error) return error.name;
  return 'UNKNOWN_ERROR';
};

type ConnectFreightProviderContextValue = {
  onConnectFreightProvider: (apiCode: string) => void;
  trackCarrierConnectionStep: (step: string, data?: Record<string, unknown>) => void;
  trackSegmentError: (error: unknown) => void;
};

export const ConnectFreightProviderContext = React.createContext<ConnectFreightProviderContextValue | undefined>(undefined);

export const useConnectFreightProviderContext = () => useContext(ConnectFreightProviderContext)!;

type ConnectFreightProviderModalContextProviderProps = {
  children: ReactNode;
  onCuiError?: (error: unknown) => void;
  onRegistrationBack?: () => void;
  onRegistrationFailure?: (error: unknown) => void;
  onRegistrationSuccess?: (sellerFreightProviderId: number | null) => void;
};

export const ConnectFreightProviderModalContextProvider = ({
  children,
  onCuiError = noop,
  onRegistrationBack = noop,
  onRegistrationFailure = noop,
  onRegistrationSuccess = noop,
}: ConnectFreightProviderModalContextProviderProps) => {
  const resolveConnectedProviderId = useResolveConnectedProviderId();
  const { close, isOpen, open } = useModal();
  const [providerApiCode, setProviderApiCode] = useState<string | undefined>(undefined);
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);
  const trackFreightConnectSucceeded = useTrackFreightConnectSucceeded();
  const trackFreightConnectFailed = useTrackFreightConnectFailed();

  const onConnectFreightProvider = useCallback(
    (apiCode: string) => {
      setProviderApiCode(apiCode);
      open();
    },
    [open],
  );

  const trackSegmentError = useCallback(
    (error: unknown) => {
      trackCarrierConnectionStep(WorkflowSteps.ERROR, { error, provider: providerApiCode, is_freight: true });
    },
    [trackCarrierConnectionStep, providerApiCode],
  );

  const onRegisterSuccess = useCallback(async () => {
    close();
    trackCarrierConnectionStep(WorkflowSteps.COMPLETE, { provider: providerApiCode, is_freight: true });
    trackFreightConnectSucceeded(getFreightProviderTrackingSlug(providerApiCode ?? ''));

    // CUI's own success callback doesn't expose the new provider id — onRegistrationSuccess
    // always resolves with a real id instead of the caller having to wait for a separate
    // refresh (resolveConnectedProviderId falls back to null if the fetch itself fails, which
    // callers treat as "connected, but couldn't confirm which account" rather than a failure).
    const sellerFreightProviderId = await resolveConnectedProviderId();

    onRegistrationSuccess(sellerFreightProviderId);
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.notificationTitle} />,
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      content: <FormattedMessage {...messages.notificationBody} />,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }, [close, trackCarrierConnectionStep, trackFreightConnectSucceeded, onRegistrationSuccess, resolveConnectedProviderId, providerApiCode]);

  const onRegisterFailure = useCallback(
    (error: unknown) => {
      close();
      trackSegmentError(error);
      trackFreightConnectFailed(getFreightProviderTrackingSlug(providerApiCode ?? ''), getSafeErrorReason(error));
      onRegistrationFailure(error);
    },
    [close, trackSegmentError, trackFreightConnectFailed, providerApiCode, onRegistrationFailure],
  );

  const onError = useCallback(
    (error: unknown) => {
      trackSegmentError(error);
      trackFreightConnectFailed(getFreightProviderTrackingSlug(providerApiCode ?? ''), getSafeErrorReason(error));
      onCuiError(error);
    },
    [trackSegmentError, trackFreightConnectFailed, providerApiCode, onCuiError],
  );

  const onConnectCancel = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.CANCEL, { provider: providerApiCode, is_freight: true });
    close();
  }, [close, trackCarrierConnectionStep, providerApiCode]);

  // CUI's own "Back" link always unmounts its registration tree (see DirectCarrierRegistration's
  // onDone), regardless of what onBack does — so the host must close its modal here too, or the
  // modal chrome stays open around a now-empty body.
  const onConnectBack = useCallback(() => {
    close();
    onRegistrationBack();
  }, [close, onRegistrationBack]);

  const value = useMemo(
    () => ({
      onConnectFreightProvider,
      trackCarrierConnectionStep,
      trackSegmentError,
    }),
    [onConnectFreightProvider, trackCarrierConnectionStep, trackSegmentError],
  );

  return (
    <ConnectFreightProviderContext.Provider value={value}>
      {children}
      {isOpen && (
        <FreightProviderRegistrationModal
          onBack={onConnectBack}
          onClose={onConnectCancel}
          onError={onError}
          onRegisterFailure={onRegisterFailure}
          onRegisterSuccess={onRegisterSuccess}
          providerApiCode={providerApiCode}
        />
      )}
    </ConnectFreightProviderContext.Provider>
  );
};
